'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { BatchHomeData, fetchBatchHomeData, fetchFeaturedPosts, fetchNewestPosts, fetchProducts } from '@/app/service/homeService/batchHomeData';
import { useUser } from '@/app/hooks/useUser';
import { useGlobalPostState, PostUpdateType, PostUpdate } from './global-post-state-context';

interface HomeDataContextType {
    homeData: BatchHomeData | null;
    isLoading: boolean;
    isInitialLoading: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    updateProduct: (updatedProduct: any) => void;
    removeFeaturedPost: (postId: string) => void;
    removeNewestPost: (postId: string) => void;
    removeProduct: (productId: string) => void;
    refreshFeaturedPosts: () => Promise<void>;
    refreshNewestPosts: () => Promise<void>;
    refreshProducts: () => Promise<void>;
    updateUserFollowStatus: (userId: string, isFollowing: boolean) => void;
}

const HomeDataContext = createContext<HomeDataContextType | undefined>(undefined);

/**
 * Home Data Provider
 *
 * Provides batched home page data to all child components
 * This eliminates the need for each component to make separate API calls
 *
 * Usage:
 * Wrap your home page with this provider and access data via useHomeData() hook
 */
export function HomeDataProvider({ children }: { children: React.ReactNode }) {
    const [homeData, setHomeData] = useState<BatchHomeData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { user, auth } = useUser(); // Get user and auth to pass owner_id and track token changes
    const globalPostState = useGlobalPostState(); // Get global post state
    const lastRequestIdRef = useRef<number>(0);

    const fetchData = async () => {
        const requestId = ++lastRequestIdRef.current;
        try {
            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setIsLoading(true);
            setError(null);

            const startTime = Date.now();
            const data = await fetchBatchHomeData(
                abortControllerRef.current.signal,
                user?.id // IMPORTANT: Pass user ID to filter hidden posts
            );

            // Only update if this is the last request we started
            if (requestId === lastRequestIdRef.current) {
                setHomeData(data);
                setIsLoading(false);
                setIsInitialLoading(false);
            }
        } catch (err: any) {
            // If it's a canceled request, we only clear flags if no other request is pending
            const isLastRequest = requestId === lastRequestIdRef.current;
            if (isLastRequest) {
                setError(err.name !== 'AbortError' ? err : null);
                setIsLoading(false);
                setIsInitialLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();

        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [user?.id, auth?.accessToken]);

    // Subscribe to global post state updates
    useEffect(() => {
        const unsubscribe = globalPostState.subscribe((update: PostUpdate) => {
            // Ignore updates from this context to prevent loops
            if (update.source === 'HomeDataContext') return;
            switch (update.type) {
                case PostUpdateType.FOLLOW_STATUS_CHANGED: {
                    const { userId, isFollowing } = update.payload as any;
                    updateUserFollowStatusLocal(userId, isFollowing);
                    break;
                }
                case PostUpdateType.USER_BLOCKED: {
                    const { userId } = update.payload as any;
                    removePostsByUserIdLocal(userId);
                    break;
                }
                case PostUpdateType.POST_DELETED: {
                    const { postId } = update.payload as any;
                    removeFeaturedPostLocal(postId);
                    removeNewestPostLocal(postId);
                    break;
                }
                case PostUpdateType.POST_UPDATED: {
                    const { post } = update.payload as any;
                    updatePostInHomeData(post);
                    break;
                }
                case PostUpdateType.POST_HIDDEN: {
                    const { postId } = update.payload as any;
                    removeFeaturedPostLocal(postId);
                    removeNewestPostLocal(postId);
                    break;
                }
                case PostUpdateType.POST_FIELD_UPDATED: {
                    const { postId, field, value } = update.payload as any;
                    updatePostFieldInHomeData(postId, field, value);
                    break;
                }
                default:
                    break;
            }
        });

        return unsubscribe;
    }, [globalPostState]);

    const refresh = async () => {
        await fetchData();
    };

    // ==================== Local State Update Helpers ====================
    // These update local state without dispatching to global (to prevent loops)

    const updateUserFollowStatusLocal = (userId: string, isFollowing: boolean) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            const updatePosts = (posts: any[]) =>
                posts.map((post) =>
                    post.owner?.id === userId
                        ? { ...post, owner: { ...post.owner, isFollowing } }
                        : post
                );
            return {
                ...prevData,
                featuredPosts: updatePosts(prevData.featuredPosts),
                newestPosts: updatePosts(prevData.newestPosts),
                products: updatePosts(prevData.products)
            };
        });
    };

    const removePostsByUserIdLocal = (userId: string) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            const removePosts = (posts: any[]) =>
                posts.filter((post) => String(post.owner?.id) !== String(userId));
            return {
                ...prevData,
                featuredPosts: removePosts(prevData.featuredPosts),
                newestPosts: removePosts(prevData.newestPosts),
                products: removePosts(prevData.products),
                news: removePosts(prevData.news),
            };
        });
    };

    const removeFeaturedPostLocal = (postId: string) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            return {
                ...prevData,
                featuredPosts: prevData.featuredPosts.filter((post) => post.id !== postId),
            };
        });
    };

    const removeNewestPostLocal = (postId: string) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            return {
                ...prevData,
                newestPosts: prevData.newestPosts.filter((post) => post.id !== postId),
            };
        });
    };

    const updatePostInHomeData = (updatedPost: any) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            const updatePosts = (posts: any[]) =>
                posts.map((post) => (post.id === updatedPost.id ? updatedPost : post));
            return {
                ...prevData,
                featuredPosts: updatePosts(prevData.featuredPosts),
                newestPosts: updatePosts(prevData.newestPosts),
                products: updatePosts(prevData.products),
                news: updatePosts(prevData.news),
            };
        });
    };

    const updatePostFieldInHomeData = (postId: string, field: string, value: any) => {
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            const updatePosts = (posts: any[]) =>
                posts.map((post) =>
                    post.id === postId ? { ...post, [field]: value } : post
                );
            return {
                ...prevData,
                featuredPosts: updatePosts(prevData.featuredPosts),
                newestPosts: updatePosts(prevData.newestPosts),
                products: updatePosts(prevData.products),
                news: updatePosts(prevData.news),
            };
        });
    };

    // ==================== Public Methods (dispatch to global) ====================

    const updateProduct = (updatedProduct: any) => {
        // Update local state
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            return {
                ...prevData,
                products: prevData.products.map((product) =>
                    product.id === updatedProduct.id ? updatedProduct : product
                ),
            };
        });
        // Dispatch to global
        globalPostState.updatePost(updatedProduct, 'HomeDataContext');
    };

    const removeFeaturedPost = (postId: string) => {
        // Update local state
        removeFeaturedPostLocal(postId);
        // Dispatch to global
        globalPostState.deletePost(postId, 'HomeDataContext');
    };

    const removeNewestPost = (postId: string) => {
        // Update local state
        removeNewestPostLocal(postId);
        // Dispatch to global
        globalPostState.deletePost(postId, 'HomeDataContext');
    };

    const removeProduct = (productId: string) => {
        // Update local state
        setHomeData((prevData) => {
            if (!prevData) return prevData;
            return {
                ...prevData,
                products: prevData.products.filter((product) => product.id !== productId),
            };
        });
        // Dispatch to global
        globalPostState.deletePost(productId, 'HomeDataContext');
    };

    const refreshFeaturedPosts = async () => {
        try {
            // Create new abort controller for this specific request
            const controller = new AbortController();
            // IMPORTANT: Pass user ID to filter hidden posts
            const newPosts = await fetchFeaturedPosts(controller.signal, user?.id);
            setHomeData((prevData) => {
                if (!prevData) return prevData;
                return {
                    ...prevData,
                    featuredPosts: newPosts,
                };
            });
        } catch (error: any) {
            // Don't update state on error - keep existing posts
            if (error.name !== 'AbortError') {
                console.error('[Context] Non-abort error:', error.message);
            }
        }
    };

    const refreshNewestPosts = async () => {
        try {
            // Create new abort controller for this specific request
            const controller = new AbortController();
            const newPosts = await fetchNewestPosts(controller.signal, user?.id);
            setHomeData((prevData) => {
                if (!prevData) return prevData;
                return {
                    ...prevData,
                    newestPosts: newPosts,
                };
            });
        } catch (error: any) {
            // Don't update state on error - keep existing posts
            if (error.name !== 'AbortError') {
                console.error('[Context] Non-abort error:', error.message);
            }
        }
    };

    const refreshProducts = async () => {
        try {
            // Create new abort controller for this specific request
            const controller = new AbortController();
            const newProducts = await fetchProducts(controller.signal, user?.id);
            setHomeData((prevData) => {
                if (!prevData) return prevData;
                return {
                    ...prevData,
                    products: newProducts,
                };
            });
        } catch (error: any) {
            // Don't update state on error - keep existing products
            if (error.name !== 'AbortError') {
                console.error('[Context] Non-abort error:', error.message);
            }
        }
    };

    const updateUserFollowStatus = (userId: string, isFollowing: boolean) => {
        // Update local state
        updateUserFollowStatusLocal(userId, isFollowing);
        // Dispatch to global
        globalPostState.updateUserFollowStatus(userId, isFollowing, 'HomeDataContext');
    };

    return (
        <HomeDataContext.Provider value={{ homeData, isLoading, isInitialLoading, error, refresh, updateProduct, removeFeaturedPost, removeNewestPost, removeProduct, refreshFeaturedPosts, refreshNewestPosts, refreshProducts, updateUserFollowStatus }}>
            {children}
        </HomeDataContext.Provider>
    );
}

/**
 * Hook to access home data from context
 *
 * @throws Error if used outside of HomeDataProvider
 * @returns HomeDataContextType with batched home data
 *
 * @example
 * const { homeData, isLoading } = useHomeData();
 * if (isLoading) return <Skeleton />;
 * return <div>{homeData.banners.map(...)}</div>;
 */
export function useHomeData(): HomeDataContextType {
    const context = useContext(HomeDataContext);

    if (context === undefined) {
        throw new Error('useHomeData must be used within a HomeDataProvider');
    }

    return context;
}
